import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { TLocalization } from '../entities/language.interface';

@Injectable({ providedIn: 'root' })
export class LanguageService {

  public http = inject(HttpClient);
  private readonly appConfig = inject(APP_CONFIG);
  private readonly apiUrl = this.appConfig.apiUrl;
  private readonly appUrl = this.appConfig.appUrl;

  public getLocalization(): Observable<TLocalization> {
    return this.http.get<TLocalization>(`${this.apiUrl.account_service}/Localization/Detect`);
  }

  /**
   * Mirrors the chosen language into the accounts app, which lives on its own
   * origin and therefore has its own `localStorage`.
   *
   * The iframe points at a static sync page (`/assets/language-sync.html`),
   * never at the SPA itself: booting the accounts app here made it load
   * `/assets/i18n/<language>.json` for the language still in its storage — the
   * previously synced one — a few hundred ms after every switch. The page reads
   * the language off the query string, so there is nothing to post to it.
   */
  public setLanguageInAccountApp(language: string) {
    if (location.href.startsWith(this.appUrl.accounts)) return;
    // The face-detection app is a kiosk with its own language; it never syncs.
    if (this.appUrl.face_detection_app && location.href.startsWith(this.appUrl.face_detection_app)) return;
    // The public registration app is anonymous: it never syncs a language into the accounts origin.
    const registrationApp = this.appConfig.externalUrl?.registration_app;
    if (registrationApp && location.href.startsWith(registrationApp)) return;
    const accountsUrl = this.appUrl.accounts.replace(/\/+$/, '');
    const iframe = document.createElement('iframe');
    iframe.src = `${accountsUrl}/assets/language-sync.html?lang=${encodeURIComponent(language)}`;
    iframe.style.display = 'none';
    iframe.onload = () => setTimeout(() => iframe.remove());
    document.body.appendChild(iframe);
  }
}
